import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from 'oneok/environments/environment';
import { DocusignService, StorageService } from '@core/services';
import { AuthService } from '@customer/services';
import { DOCUMENT } from '@angular/common';
import { catchError, take, throwError } from 'rxjs';
import posthog from 'posthog-js';

export const auth: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const requestUrl = req.url;
  const storageSvc = inject(StorageService);
  const authSvc = inject(AuthService);
  let _isLoggedIn = false;
  storageSvc.currentUser$.subscribe(user => (_isLoggedIn = !!user));

  if (isStrapiReq(requestUrl)) {
    const strapiConfig = environment.strapi as {
      accessToken?: string;
      dev?: { accessToken: string };
      prod?: { accessToken: string };
    };
    const strapiToken = (strapiConfig.accessToken ??
      (document.URL.indexOf('localhost:') >= 0 ? strapiConfig.dev?.accessToken : strapiConfig.prod?.accessToken))!;
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${strapiToken}`
      }
    });
  } else if (
    requestUrl.toLowerCase().includes('/login') ||
    requestUrl.toLowerCase().includes('modules') ||
    requestUrl.toLowerCase().includes('accounts')
  ) {
    if (inject(DOCUMENT).defaultView?.sessionStorage.getItem('authToken')) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${authSvc.getAuthToken()}`
        }
      });
    }
  } else if (_isLoggedIn) {
    const jwt = authSvc.getAuthToken();
    if (isPortalDocusignApi(requestUrl)) {
      const docusignToken = inject(DocusignService).getAccessTokenFromCookie();
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${jwt}`,
          DSAccessToken: docusignToken
        }
      });
    } else if (isPortalApi(requestUrl)) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${jwt}`
        }
      });
    }
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      const _isHttpErrorResponse = error instanceof HttpErrorResponse;
      const _is401 = _isHttpErrorResponse && error.status === 401;
      const _isLogoutable = _is401 && (isPortalDocusignApi(requestUrl) || isPortalApi(requestUrl));
      const errorDetails = isErrorDetails(error) ? error : {};
      storageSvc?.currentAccount$.pipe(take(1)).subscribe(currAcc => {
        posthog.capture('Exception', {
          account: currAcc?.AccountName ?? '',
          status: errorDetails.status,
          apiUrl: errorDetails.url,
          message: errorDetails.message
        });
      });

      if (_isLogoutable) {
        authSvc.logoutUser();
      }
      console.error(`API Request Error: ${errorDetails.status} | ${errorDetails.statusText}`);
      return throwError(() => error);
    })
  );
};

const isErrorDetails = (error: unknown): error is Partial<HttpErrorResponse> => {
  return typeof error === 'object' && error !== null;
};
const isStrapiReq = (requestUrl: string): boolean => {
  return ['content.dynlab.oneok.', 'content.dyntest.oneok.', 'content.dynqa.oneok.', 'content.dyn.oneok.'].some(
    (strapiFqdnPart: string): boolean => {
      return requestUrl.includes(strapiFqdnPart);
    }
  );
};
const isPortalApi = (requestUrl: string): boolean => {
  //dev local dev api
  if (requestUrl.includes('https://localhost:7234')) {
    return true;
  }
  return (
    (requestUrl.startsWith(environment.apiUrl) || requestUrl.startsWith(environment.apiUrlV2)) &&
    !requestUrl.includes('docusign')
  );
};
const isPortalDocusignApi = (requestUrl: string): boolean => {
  return requestUrl.startsWith(environment.apiUrl) && requestUrl.includes('docusign');
};
